import type { StreamCallback, StreamChunk } from './types';

export interface MockStreamOptions {
  wordsPerChunk?: number;
  baseDelayMs?: number;
  jitterMs?: number;
  thinkDelayMs?: number;
}

export function createMockStream(
  fullText: string,
  callback: StreamCallback,
  options?: MockStreamOptions,
): { cancel: () => void } {
  const wordsPerChunk = options?.wordsPerChunk ?? 3;
  const baseDelayMs = options?.baseDelayMs ?? 30;
  const jitterMs = options?.jitterMs ?? 20;
  const thinkDelayMs = options?.thinkDelayMs ?? 400;

  // Split into words while preserving whitespace
  const words = fullText.split(/(\s+)/);
  const chunks: StreamChunk[] = [];
  let buffer = '';

  for (let i = 0; i < words.length; i++) {
    buffer += words[i];
    // Emit every N actual words (not whitespace tokens) or at end
    const isEnd = i === words.length - 1;
    if ((i + 1) % (wordsPerChunk * 2) === 0 || isEnd) {
      const delay = baseDelayMs + Math.random() * jitterMs;
      chunks.push({ text: buffer, done: isEnd, delay });
      buffer = '';
    }
  }

  // Handle edge case: empty text
  if (chunks.length === 0) {
    chunks.push({ text: '', done: true, delay: 0 });
  }

  let cancelled = false;
  let accumulated = '';
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const emitNext = (index: number) => {
    if (cancelled || index >= chunks.length) return;
    const chunk = chunks[index];
    accumulated += chunk.text;
    callback({ text: accumulated, done: chunk.done, delay: chunk.delay });
    if (!chunk.done) {
      timeoutId = setTimeout(() => emitNext(index + 1), chunk.delay);
    }
  };

  // Initial "thinking" delay
  const thinkJitter = Math.random() * 600;
  timeoutId = setTimeout(() => emitNext(0), thinkDelayMs + thinkJitter);

  return {
    cancel: () => {
      cancelled = true;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    },
  };
}
